// One template form, two pages (template-page spec §3-4): every field,
// images included, feeds one draft, and Save is the single moment anything
// reaches a live survey. Edit and create differ only in where Save sends the
// body and what happens after, so both pages bind through this hook.

import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import type { FieldErrors } from "react-hook-form";
import { ApiError } from "@/api/error";
import { fieldErrorsSummary } from "@/api/friendly-error";
import type { TemplateBody } from "@/api/templates";
import type { Template } from "@/api/types";
import { toastFormError, toastMutationError } from "@/components/error-toast";
import { useAssetClientContext } from "@/features/assets/asset-client-context";
import { resolveArtworkUrls } from "@/features/assets/resolve-artwork-urls";
import { firstFieldMessage, useKoreForm } from "@/lib/forms";
import { useSaveBar } from "@/lib/save-bar";
import { type TemplateValues, templateSchema, toBody } from "./template-schema";

export type TemplateForm = ReturnType<typeof useKoreForm<typeof templateSchema>>;

export interface UseTemplateFormOptions {
    defaultValues: TemplateValues;
    /** Persists the body; resolves with what the server stored. */
    persist: (body: TemplateBody) => Promise<Template>;
    /** After a successful save, with the stored template. */
    onSaved: (template: Template, form: TemplateForm) => void;
    /** The toast's title when the save fails, e.g. "Save failed". */
    failureTitle: string;
    /** Save is worth sending before any edit (the create page's defaults).
     *  Otherwise a submit with nothing edited — Enter on a clean page — is a
     *  no-op, as the bar's disabled Save says. */
    armed?: boolean;
}

export function useTemplateForm({
    defaultValues,
    persist,
    onSaved,
    failureTitle,
    armed = false,
}: UseTemplateFormOptions) {
    const form = useKoreForm({ schema: templateSchema, defaultValues });
    // Watched, not read once: the preview follows every keystroke, and the
    // Type group's "Custom…" input appears with the font choice.
    const values = form.watch();
    // Read in render so react-hook-form computes them (see create-survey-page).
    const { isDirty, errors } = form.formState;
    const bar = useSaveBar({ invalid: Object.keys(errors).length > 0 });
    // Shared with the artwork picker's asset-client context, so a manifest it
    // already fetched costs nothing to read again at submit.
    const assetCtx = useAssetClientContext();
    const queryClient = useQueryClient();
    // One submit at a time. The bar's Save disables while saving, but Enter
    // reaches the form through its hidden submit button regardless, and a
    // second create would make a second template. A ref, because state only
    // lands on the next render.
    const inFlightRef = useRef(false);

    async function submit(submitted: TemplateValues) {
        if (inFlightRef.current || !(armed || form.formState.isDirty)) return;
        inFlightRef.current = true;
        bar.submitting();
        try {
            // Artwork is stored as a public render URL beside its asset id: the
            // widget is public and can't read a delivery manifest itself.
            // `dirtyFields`, read now (at submit, as the old editor did), tells
            // the resolver whether a failure may leave the stored URL alone or
            // must clear it.
            const { dirtyFields } = form.formState;
            const artwork = await resolveArtworkUrls(assetCtx, queryClient, {
                backgroundImage: {
                    assetId: submitted.backgroundImageAssetId || null,
                    changed: Boolean(dirtyFields.backgroundImageAssetId),
                },
                logo: { assetId: submitted.logoAssetId || null, changed: Boolean(dirtyFields.logoAssetId) },
            });
            onSaved(await persist({ ...toBody(submitted), ...artwork }), form);
            bar.saved();
        } catch (err) {
            // Not the server's answer (artwork resolution, say): let it surface.
            if (!(err instanceof ApiError)) {
                toastMutationError(failureTitle, err);
                throw err;
            }
            // Field violations land on their fields, summed up in the bar;
            // the toast says the rest.
            if (toastFormError(failureTitle, form, err)) bar.blocked(fieldErrorsSummary(err) ?? "");
        } finally {
            inFlightRef.current = false;
        }
    }

    function onInvalid(fieldErrors: FieldErrors) {
        bar.blocked(firstFieldMessage(fieldErrors));
    }

    return {
        form,
        values,
        isDirty,
        error: bar.errorMessage,
        // For `<Form onSubmit onInvalid>`: Enter in a field saves, as before.
        submit,
        onInvalid,
        // For the save bar, which sits outside the form.
        save: () => void form.handleSubmit(submit, onInvalid)(),
        // Back to the last save, and the bar stops naming a failure of edits
        // that are gone.
        discard: () => {
            form.reset();
            bar.reset();
        },
    };
}

export type TemplateFormBinding = ReturnType<typeof useTemplateForm>;
